import { useState } from 'react'
import { Loader2, RefreshCw } from 'lucide-react'
import { metricsApiClient } from '../../api/http/wireApiClient'

type ProbeState = {
  status: 'idle' | 'loading' | 'done' | 'error'
  data: Record<string, unknown> | unknown[] | null
  error: string | null
  durationMs: number | null
}

const PROBE_ENDPOINTS: { key: string; label: string; path: string; isoDate?: boolean; extraParams?: Record<string, string> }[] = [
  { key: 'searches',                    label: '/metrics/searches',                    path: '/metrics/searches' },
  { key: 'article-workflows',           label: '/metrics/article-workflows',           path: '/metrics/article-workflows' },
  { key: 'clients',                     label: '/metrics/clients',                     path: '/metrics/clients' },
  { key: 'services',                    label: '/metrics/services',                    path: '/metrics/services' },
  { key: 'article-nav-methods',          label: '/metrics/article/nav-methods',         path: '/metrics/article/nav-methods' },
  { key: 'perf-authentication',         label: '/performance/authentication',          path: '/performance/authentication', isoDate: true },
  { key: 'perf-search',                 label: '/performance/search',                  path: '/performance/search', isoDate: true, extraParams: { searchType: 'ARTICLES' } },
]

function formatDateParam(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00`
}

export function EndpointsProbe() {
  const [states, setStates] = useState<Record<string, ProbeState>>(
    Object.fromEntries(
      PROBE_ENDPOINTS.map(e => [e.key, { status: 'idle', data: null, error: null, durationMs: null }])
    )
  )

  async function runProbes() {
    const now = new Date()
    const past = new Date(now)
    past.setDate(past.getDate() - 90)
    const from = formatDateParam(past)
    const to = formatDateParam(now)
    const fromIso = formatDateParam(past) + 'Z'
    const toIso = formatDateParam(now) + 'Z'

    setStates(prev =>
      Object.fromEntries(
        Object.keys(prev).map(k => [k, { status: 'loading', data: null, error: null, durationMs: null }])
      )
    )

    await Promise.all(PROBE_ENDPOINTS.map(async ({ key, path, isoDate, extraParams }) => {
      const start = Date.now()
      try {
        const res = await metricsApiClient.get(path, { params: { from: isoDate ? fromIso : from, to: isoDate ? toIso : to, ...extraParams } })
        setStates(prev => ({ ...prev, [key]: { status: 'done', data: res.data, error: null, durationMs: Date.now() - start } }))
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        setStates(prev => ({ ...prev, [key]: { status: 'error', data: null, error: msg, durationMs: Date.now() - start } }))
      }
    }))
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="font-sans text-xs text-fg-secondary">90-day window · dev environment</p>
        <button
          type="button"
          onClick={runProbes}
          className="flex items-center gap-1.5 rounded border border-line px-3 py-1.5 font-sans text-[11px] font-semibold text-fg-secondary transition-colors hover:border-line-strong hover:bg-fill hover:text-fg"
        >
          <RefreshCw size={11} />
          Probe Endpoints
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {PROBE_ENDPOINTS.map(({ key, label }) => {
          const s = states[key] ?? { status: 'idle' as const, data: null, error: null, durationMs: null }
          const rowCount = Array.isArray(s.data) ? s.data.length : s.data !== null ? 1 : null
          return (
            <div key={key} className="rounded border border-line bg-surface p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-mono text-[11px] font-bold text-fg">{label}</p>
                {s.durationMs !== null && (
                  <span className="font-mono text-[10px] text-fg-secondary">{s.durationMs}ms</span>
                )}
              </div>

              {s.status === 'idle' && (
                <p className="font-sans text-[11px] text-fg-secondary">Not yet called</p>
              )}
              {s.status === 'loading' && (
                <div className="flex items-center gap-2 text-fg-secondary">
                  <Loader2 size={12} className="animate-spin" />
                  <span className="font-sans text-[11px]">Fetching…</span>
                </div>
              )}
              {s.status === 'error' && (
                <p className="font-sans text-[11px] text-error-fg">{s.error}</p>
              )}
              {s.status === 'done' && (
                <div className="space-y-2">
                  <p className="font-sans text-[11px] text-fg-secondary">
                    {rowCount !== null ? (
                      <><span className="font-bold text-fg">{rowCount}</span> {rowCount === 1 ? 'record' : 'records'}</>
                    ) : (
                      'Response received'
                    )}
                  </p>
                  <pre className="max-h-56 overflow-auto rounded bg-fill p-2 font-mono text-[10px] leading-relaxed text-fg whitespace-pre-wrap break-all">
                    {JSON.stringify(s.data, null, 2).slice(0, 2000)}
                    {JSON.stringify(s.data, null, 2).length > 2000 ? '\n…truncated' : ''}
                  </pre>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
